import React, { forwardRef } from 'react';
import classnames from 'classnames';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import type { PolymorphicForwardRefRenderFunc, PolymorphicComponentWithRef } from '@hh.ru/magritte-types';
import { BadgeSize, ComponentWithBadge } from '@hh.ru/magritte-ui-badge';
import { useButton } from '@hh.ru/magritte-ui-button/aria';
import type { ButtonProps } from '@hh.ru/magritte-ui-button/types';
import { Loader } from '@hh.ru/magritte-ui-loader';

import styles from './button.less';

const ALLOWED_BADGE_SIZES: BadgeSize[] = ['small', 'extra-small'];

const ButtonForwardRefRenderFunc: PolymorphicForwardRefRenderFunc<ButtonProps, 'button'> = (props, ref) => {
    const {
        'aria-label': ariaLabel,
        children,
        postfix,
        Element = 'button',
        hideLabel = false,
        icon,
        loading,
        mode,
        size = 'medium',
        stretched,
        style = 'neutral',
        subcaption,
        type = 'button',
        disabled,
        badge,
        ...buttonProps
    } = props;

    const forceDisabled = useDisabled();
    const { ariaProps: additionalAttributes } = useButton({ disabled: !!disabled || forceDisabled, Element });

    if (Element === 'button') {
        additionalAttributes.type = type;
    }

    if (ref) {
        /**
         * Element может быть простой функцией поэтому напрямую
         * нельзя назначать ref
         * А может быть forwardRef, ClassComponent и простой тег,
         * поэтому в обход типизации назначаем ref как unknown
         */
        additionalAttributes.ref = ref;
    }

    const withSubcaption = size !== 'small' && subcaption;

    const button = (
        <Element
            className={classnames(styles.button, {
                [styles[`button_mode-${mode}`]]: mode,
                [styles[`button_size-${size}`]]: size,
                [styles.button_stretched]: stretched,
                [styles.button_subcaption]: withSubcaption,
                [styles.button_textless]: !withSubcaption && (hideLabel || !children) && !postfix,
                [styles.button_loading]: loading,
                [styles[`button_style-${style}`]]: style,
                [styles.buttonWithIcon]: !!icon,
            })}
            disabled={forceDisabled || !!disabled}
            tabIndex={loading ? -1 : undefined}
            {...additionalAttributes}
            {...buttonProps}
        >
            <div className={styles.buttonView}>
                <span className={styles.button__content}>
                    {postfix && (
                        <span
                            className={classnames(styles.buttonPostfix, {
                                [styles[`button-postfix_size-${size}`]]: size,
                            })}
                        >
                            {postfix}
                        </span>
                    )}
                    {icon && (
                        <span className={styles.button__icon}>
                            {React.cloneElement(
                                icon,
                                hideLabel ? { 'aria-label': ariaLabel } : { 'aria-hidden': true, focusable: false }
                            )}
                        </span>
                    )}
                    {children && !hideLabel && <span className={styles.button__label}>{children}</span>}
                    {withSubcaption && <span className={styles.button__subcaption}>{subcaption}</span>}
                    {loading && (
                        <span className={styles.button__loading}>
                            <Loader />
                        </span>
                    )}
                </span>
            </div>
        </Element>
    );

    if (badge && !disabled && !forceDisabled) {
        return (
            <ComponentWithBadge allowedSizes={ALLOWED_BADGE_SIZES} badge={badge}>
                {button}
            </ComponentWithBadge>
        );
    }

    return button;
};

export const Button: PolymorphicComponentWithRef<ButtonProps, 'button'> = forwardRef(ButtonForwardRefRenderFunc);

export const STRETCHED_WRAPPER_CLASS = 'magritte-button-stretched-wrapper';
